import { Attachment, Entity, EntityLink, Location, NotifiableContactEntity, WithConnectStamp } from '../index';
import { ActivityEntityName } from './activity.entity.name';
import { ActivityKind, ActivityType } from './activity.type';


export type CrmEntityName = ActivityEntityName.Customer | ActivityEntityName.Supplier | ActivityEntityName.Contractor | ActivityEntityName.ContactEmployee | ActivityEntityName.LoginContact | ActivityEntityName.BasicContact;

export enum ActivitySource {
    Id = 'id',
    Tenant = 'tenant',
    Compliance = 'compliance',
    ClientPortal = 'clients',
    ContractorPortal = 'contractors',
    Mobile = 'mobile',
    Connect = 'connect'
}

export enum ActivityDisplayMode {
    ReadOnly = 'ReadOnly',
    Edit = 'Edit',
    New = 'New'
}

export enum ActivityGroupName {
    Checklist = 'Checklist'
}


export enum ActivityTagName {
    CustomerId = 'CustomerId',
    WorkOrderId = 'WorkOrderId',
    JobId = 'JobId',
    CodeId = 'CodeId',
    ChecklistId = 'ChecklistId',
    QuestionId = 'QuestionId',
    Type = 'ActivityType',
    Group = 'ActivityGroup',
    Resource = 'ResourceGroup',
    PurchaseOrderId = 'PurchaseOrderId',
    InvoiceId = 'InvoiceId',
    PrepaymentInvoiceId = 'PrepaymentInvoiceId',
    PrepaymentSupplierId = 'PrepaymentSupplierId',
    BillId = 'BillId',
    TechnicianId = 'TechnicianId',
    ComplianceId = 'ComplianceId',
    ContactId = 'ContactId',
    AllocationId = 'AllocationId',
    UtilityId = 'UtilityId',
    RecurringJobId = 'RecurrentJobId',
    ResourceId = 'ResourceId',
    Opportunity = 'Opportunity',
    ContractorId = 'ContractorId',
    Lead = 'Lead',
    QuotePro = 'QuotePro',
    Project = 'Project',
    Payment = 'Payment',
    Quote = 'Quote',
    Site = 'Site',
    ClaimContractorId = 'ClaimContractorId',
    ContractorActivityId = 'ContractorActivityId',
    CustomerActivityId = 'CustomerActivityId',
    AssetId = 'AssetId',
    WorkId = 'WorkId',
}

export enum ActivityShareType {
    None = '',
    Customer = 'Customer',
    Field = 'Field',
    Tenant = 'Tenant',
    NotShared = 'Not Shared'
}

export enum ActivitySendStatus {
    None = '',
    Pending = 'Queued',
    Sent = 'Sent',
    SentByContactor = 'Sent By Contactor',
    Failed = 'Failed',
    //Handles situation for legacy emails where we do not check the status
    //but we dont want it to be processed again anyway
    LegacySent = 'LegacySent',
}

export enum ActivityPhoneDirectionType {
    Inbound = 'Inbound',
    Outbound = 'Outbound'
}

export enum ActivityPhoneOutComeType {
    Connected = 'Connected',
    LeftMessage = 'Left Message',
    LeftVoicemail = 'Left Voicemail',
    Busy = 'Busy',
    NoAnswer = 'No Answer',
    WrongNumber = 'Wrong Number',
    InvalidNumber = 'Invalid Number'
}

export interface ActivityTag {
    name: ActivityTagName;
    value: any;
    data?: Entity;
}

export interface ActivitySyncRecipient {
    by?: string;
    date?: Date;
    error?: string;
}

export interface ActivityExport {
    _id: string;
    type: ActivityType;
    name: string;
    note: string;
    attachments?: Attachment[];
    createdDate?: Date;
    updateDate?: Date;
    sent?: ActivitySyncRecipient;
}

export interface Shareable {
    systemSharing?: boolean;
    shared?: ActivityShareType[];
    sensitive?: boolean;
}

export const sharableEquals = (a: Shareable, b: Shareable): boolean => {

    const aSystemSharing = a?.systemSharing;
    const bSystemSharing = b?.systemSharing;

    const aCustomer = a?.shared?.includes(ActivityShareType.Customer);
    const bCustomer = b?.shared?.includes(ActivityShareType.Customer);

    const aField = a?.shared?.includes(ActivityShareType.Field);
    const bField = b?.shared?.includes(ActivityShareType.Field);

    const aSensitive = a?.sensitive;
    const bSensitive = b?.sensitive;

    const systemSharing = aSystemSharing === bSystemSharing;
    const customer = aCustomer === bCustomer;
    const filed = aField === bField;
    const sensitive = aSensitive === bSensitive;

    const equal = systemSharing && customer && filed && sensitive;

    console.log('sharableEquals', equal, a, b);

    return equal;
}

export const notShared = () => {
    return {
        systemSharing: true,
        shared: [],
        sensitive: false
    }
}

export interface Activity extends Shareable, WithConnectStamp {

    _id: string;
    active: boolean;

    type: ActivityType;
    kind: ActivityKind;

    entity: ActivityEntityName;
    entityId: string;

    /** This is used for the sender an email or sms activity */
    from?: NotifiableContactEntity;

    /** This is used for the receiver an email or sms activity */
    to?: NotifiableContactEntity[];

    /** This is used for the cc receiver an email */
    cc?: NotifiableContactEntity[];

    /** This is used for the bcc receiver an email */
    bcc?: NotifiableContactEntity[];

    /** This is used to an name or the subject of and email activity */
    name?: string;

    /** This is used to as a note or the body of and email activity */
    note: string;

    subTitle?: string;

    attachments?: Attachment[];
    claimedAttachments?: Attachment[];

    createdDate?: Date;
    updateDate?: Date;
    updateBy?: Entity;
    createdBy?: Entity;

    // updatedBy?: Entity;
    system: boolean;
    tags: ActivityTag[];
    tenantName?: string;

    sensitive?: boolean;
    systemSharing?: boolean;
    shared?: ActivityShareType[];

    // createdDateString?: string;
    source?: ActivitySource;

    sent?: ActivitySyncRecipient;
    received?: ActivitySyncRecipient;

    isDraft?: boolean;

    /** This is used to store tags */
    labels?: Entity[];

    sendStatus?: ActivitySendStatus;
    sendDate?: Date;
    sendError?: string;

    /** @deprecated never really used */
    tenantId?: string;

    /** @deprecated use tags */
    parentId?: string;

    /** @deprecated use tags */
    customerId?: string;

    /** @deprecated use tags */
    questionId?: string;


    /** handle track location when field app check in/stop */
    location?: Location;

    links?: EntityLink[];
    timezone?: string;

    effectiveSharing?: Shareable;

    markEntitySent?: boolean;
    includeEntityLink?: boolean;

    callDirection?: ActivityPhoneDirectionType;
    callOutcome?: ActivityPhoneOutComeType;
    callDate?: Date;
    callDuration?: number;
}

interface SmsActivityMembers {

    _id: string;
    active: boolean;
    system: boolean;

    kind: ActivityKind;
    type: ActivityType;

    entity: ActivityEntityName;
    entityId: string;

    from: NotifiableContactEntity;
    to: NotifiableContactEntity[];

    name: string;
    note: string;

    createdDate?: Date;
    updateDate?: Date;
    updateBy?: Entity;
    createdBy?: Entity;

    sendStatus?: ActivitySendStatus;
    sendDate?: Date;
    sendError?: string;

    tags: ActivityTag[];

    sensitive?: boolean;
    shared?: ActivityShareType[];

    /*** This is used to store tags */
    labels?: Entity[];
}

interface EmailActivityMembers {

    _id: string;
    active: boolean;
    system: boolean;

    kind: ActivityKind;
    type: ActivityType;

    entity: ActivityEntityName;
    entityId: string;

    from?: NotifiableContactEntity
    to?: NotifiableContactEntity[];
    cc?: NotifiableContactEntity[];
    bcc?: NotifiableContactEntity[];

    name: string;
    note: string;
    attachments?: Attachment[];

    createdDate?: Date;
    updateDate?: Date;
    updateBy?: Entity;
    createdBy?: Entity;

    sendStatus?: ActivitySendStatus;
    sendDate?: Date;
    sendError?: string;

    tags: ActivityTag[];
    sensitive?: boolean;
    shared?: ActivityShareType[];

    /** This is used to store tags */
    labels?: Entity[];

}

export type SmsActivity = Pick<Activity, keyof SmsActivityMembers>;
export type EmailActivity = Pick<Activity, keyof EmailActivityMembers>;

export type ActivitySearchOptions =
    {
        /** This flag is used to restrict activity searching to the the entityId field only and will ignore tags eg. The customer activities in CRM */
        restrictSearchToEntityId?: boolean;
    }
    & {
        /** This flag is used to restrict activity searching to the the entityType field only and will ignore tags eg. The utility activities needs this flag */
        restrictSearchToEntityType?: boolean;
    }
    & {
        /** This flag is used to restrict activity searching to the the entityType field only and will ignore tags eg. The utility activities needs this flag */
        restrictSearchToEntityTypeAndId?: boolean;
        entityName?: ActivityEntityName;
    }
    & {
        /** This flag is used to turn on the feature to display all stage activities when getting activities in field app */
        showAllFieldActivities?: boolean;
    }
